import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import TextField from 'components/text-field';
import React, { FC } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import ContainerModal from './modal-container';
import { NewEmailType } from 'types/new-user';
import { createPartyEmail } from 'graphql/parties/mutations';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import validations from 'utils/validations';

type DialogEmailsProps = {
  open: boolean;
  onClose: () => void;
  partyId: string;
  onSuccess: (emails: NewEmailType[]) => void;
};

type createEmailForm = {
  email: string;
  kind: string;
};

const CreateEmails: FC<DialogEmailsProps> = ({ open, onClose, partyId, onSuccess }) => {
  const { t } = useTranslation();
  const { mutate: createEmail, ...responseCreateEmail } = useMutation(createPartyEmail, {
    onSuccess: (response) => {
      responseCreateEmail.reset();
      onSuccess(response.createEmail?.emails || []);
      onClose();
    },
  });
  const schema = z.object({
    email: validations.email(t('validations:invalidEmail'), t('validations:required')),
    kind: validations.requiredString(t('validations:required')),
  });
  const methods = useForm<createEmailForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      kind: '',
    },
  });

  const { control, formState: { errors } } = methods;
  const watchedKind = useWatch({ control, name: 'kind' });
  const isSaving = responseCreateEmail.isLoading;
  return (
    <ContainerModal
      open={open}
      onClose={onClose}
      onCancel={onClose}
      onSave={() => {
        methods.handleSubmit((data) => {
          createEmail({
            partyId,
            kind: data.kind,
            data: {
              address: data.email,
            } as unknown as NewEmailType,
          });
        })();
      }}
      disableSavebutton={!methods.formState.isValid || watchedKind === 'Login'}
      loadingSaveButton={isSaving}
      modalTitle={t('contact:addEmailModalTitle')}
    >
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <Controller
            control={control}
            name="email"
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                inputRef={ref}
                disabled={isSaving}
                label={t('contact:emailAddressLabel')}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
        </Grid>
        <Grid item>
          <Controller
            control={control}
            name="kind"
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                inputRef={ref}
                disabled={isSaving}
                label={t('contact:kindLabel')}
                error={!!errors.kind}
                helperText={
                  errors.kind?.message
                  || (watchedKind === 'Login' ? t('contact:loginEmailHelperText') : undefined)
                }
              />
            )}
          />
        </Grid>
      </Grid>
    </ContainerModal>
  );
};

export default CreateEmails;
